<template>
    <v-form ref="form">
        <FormOptions
            ref="formOptions"
            v-model="formData.transportOptions"
            :formType="formType"
            :title="$t('formFields.options')"
        />

        <FormInfo
            ref="formInfo"
            v-model="formData.info"
            :formType="formType"
            :title="$t('formFields.information')"
        />
        <div v-auto-animate>
            <div
                v-for="(location, index) in formData.loadingLocations"
                :key="location.rowKey"
            >
                <FormLocation
                    ref="loadingLocationFields"
                    :data-cy-prefix="`loading-location-${index}`"
                    :model-value="location"
                    :formType="formType"
                    :title="$t('formFields.loadingAt')"
                    :show-title="index === 0"
                    :show-add="index === 0"
                    :show-remove="index > 0"
                    :add-label="$t('formFields.addLoadingStop')"
                    :remove-label="$t('formFields.removeLoadingStop')"
                    @update:model-value="onStopInput('loadingLocations', index, $event)"
                    @add="addStop('loadingLocations')"
                    @remove="removeStop('loadingLocations', index)"
                />
            </div>
        </div>
        <div v-auto-animate>
            <div
                v-for="(location, index) in formData.unloadingLocations"
                :key="location.rowKey"
            >
                <FormLocation
                    ref="unloadingLocationFields"
                    :data-cy-prefix="`unloading-location-${index}`"
                    :model-value="location"
                    :formType="formType"
                    :title="$t('formFields.unloadingAt')"
                    :show-title="index === 0"
                    :show-add="index === 0"
                    :show-remove="index > 0"
                    :add-label="$t('formFields.addUnloadingStop')"
                    :remove-label="$t('formFields.removeUnloadingStop')"
                    @update:model-value="onStopInput('unloadingLocations', index, $event)"
                    @add="addStop('unloadingLocations')"
                    @remove="removeStop('unloadingLocations', index)"
                />
            </div>
        </div>
        <RouteMap
            data-cy-prefix="publish-form"
            :origin-place-id="routeOriginPlaceId"
            :destination-place-id="routeDestinationPlaceId"
        />
        <FormNotes ref="formNotes" v-model="formData.notes" />
    </v-form>

    <LoadingOverlay :model-value="submitting" />

    <Teleport to="#form-toolbar-custom-actions" :disabled="!toolbarTeleportReady">
        <FuelCalculatorMenu
            v-if="toolbarTeleportReady"
            :open="toolbarFuelCalculatorOpen"
            :price-per-liter="fuelPricePerLiter"
            :distance-km="fuelDistanceKm"
            :consumption-l-per100-km="fuelConsumptionLPer100Km"
            :applied-fuel-cost-label="appliedFuelCostLabel"
            @update:open="onToolbarFuelCalculatorOpenChange"
            @update:price-per-liter="fuelPricePerLiter = $event"
            @update:distance-km="fuelDistanceKm = $event"
            @update:consumption-l-per100-km="fuelConsumptionLPer100Km = $event"
            @apply="applyPublishFuelCost"
        />
        <FloorAreaMenu
            v-if="toolbarTeleportReady"
            :open="toolbarFloorAreaOpen"
            :length-cm="floorAreaLengthCm"
            :width-cm="floorAreaWidthCm"
            :package-count="floorAreaPackageCount"
            :applied-floor-area-label="appliedFloorAreaLabel"
            @update:open="onToolbarFloorAreaOpenChange"
            @update:length-cm="floorAreaLengthCm = $event"
            @update:width-cm="floorAreaWidthCm = $event"
            @update:package-count="floorAreaPackageCount = $event"
            @apply="applyPublishFloorArea"
        />
        <TripDurationMenu
            v-if="toolbarTeleportReady"
            variant="toolbar-button"
            show-distance-input
            :distance-km="calculatorDistanceKm"
            :speed-kmh="calculatorSpeedKmh"
            :open="toolbarCalculatorOpen"
            :applied-trip-duration-label="appliedTripDurationLabel"
            @update:open="onToolbarCalculatorOpenChange"
            @update:distance-km="calculatorDistanceKm = $event"
            @update:speed-kmh="calculatorSpeedKmh = $event"
            @apply="applyPublishTripDuration"
        />
    </Teleport>

    <v-divider class="pb-4"></v-divider>
    <!--        <v-row>-->
    <!--            <v-col>-->
    <!--                <v-btn @click="fillForm">FILL FORM</v-btn>-->
    <!--            </v-col>-->
    <!--        </v-row>-->
</template>

<script>
import { nextTick } from "vue";
import { v4 as uuidv4 } from "uuid";
import FormInfo from "./FormInfo.vue";
import FormLocation from "./FormLocation.vue";
import FormNotes from "./FormNotes.vue";
import FormOptions from "./FormOptions.vue";
import RouteMap from "./RouteMap.vue";
import TripDurationMenu from "@/components/entries/TripDurationMenu.vue";
import FloorAreaMenu from "@/components/entries/FloorAreaMenu.vue";
import FuelCalculatorMenu from "@/components/entries/FuelCalculatorMenu.vue";
import { mapMutations, mapState } from "vuex";
import FormActions from "./FormActions.vue";
import LoadingOverlay from "@/components/display/LoadingOverlay.vue";
import axios from "axios"; // Ensure axios is installed via npm
import { mapEnumValue, showAlertModal } from "@/utils";
import {
    PAYMENT_DEADLINE,
    TRUCK_COATING,
    TRUCK_TRANSPORT_TYPE,
    TRUCK_SPECIAL_FEATURE,
    TRUCK_TYPE,
} from "@/enums/truckEnums.js";
import emitter from "@/eventBus";

export default {
    props: {
        formType: String,
        required: true,
        editMode: {
            type: Boolean,
            default: false,
        },
        entriesData: {
            type: Object,
            default: null,
        },
    },
    components: {
        FormInfo,
        FormLocation,
        FormNotes,
        FormOptions,
        TripDurationMenu,
        FloorAreaMenu,
        FuelCalculatorMenu,
        FormActions,
        RouteMap,
        LoadingOverlay,
    },
    data() {
        return {
            submitting: false,
            calculatorDistanceKm: "",
            calculatorSpeedKmh: "",
            floorAreaLengthCm: "",
            floorAreaWidthCm: "",
            floorAreaPackageCount: "1",
            fuelPricePerLiter: "",
            fuelDistanceKm: "",
            fuelConsumptionLPer100Km: "",
            appliedFloorAreaLabel: "",
            appliedTripDurationLabel: "",
            appliedFuelCostLabel: "",
            toolbarCalculatorOpen: false,
            toolbarFloorAreaOpen: false,
            toolbarFuelCalculatorOpen: false,
            toolbarTeleportReady: false,
            formData:
                this.editMode && this.entriesData
                    ? {
                          ...this.entriesData,
                          transportOptions: {
                              truckTransportType:
                                  this.entriesData.transport_options
                                      ?.truck_transport_type || null,
                              truckSpecialFeatures:
                                  this.entriesData.transport_options
                                      ?.truck_special_features || [],
                              truckCoating:
                                  this.entriesData.transport_options
                                      ?.truck_coating || null,
                              truckType:
                                  this.entriesData.transport_options
                                      ?.truck_type || null,
                              platformType:
                                  this.entriesData.transport_options
                                      ?.platform_type || [],
                              dangerousLoad:
                                  this.entriesData.transport_options
                                      ?.dangerous_load || false,
                              escrowAccount:
                                  this.entriesData.transport_options
                                      ?.escrow_account || false,
                          },
                      } // Use existing data if editing
                    : {
                          uid: uuidv4(),
                          formType: this.formType,
                          // Initialize with empty/default values
                          loadingLocations: [this.createEmptyLocation()],
                          unloadingLocations: [this.createEmptyLocation()],
                          info: {
                              weight: "",
                              volume: "",
                              mdp: "",
                              price: "",
                              paymentTerm: 1,
                              avgSpeedKmh: "",
                          },
                          notes: "",
                          transportOptions: {
                              truckSpecialFeatures: [],
                              truckTransportType: 1,
                              truckCoating: 1,
                              dangerousLoad: false,
                              platformType: [],
                          },
                      },
            truckRequests: [],
            wareRequests: [],
        };
    },
    computed: {
        ...mapState(["isMobile", "formButtons"]),
        submitRequested() {
            return this.$store.state.submitRequested;
        },
        routeOriginPlaceId() {
            return this.routeEndpoint("loadingLocations")?.place_id || "";
        },
        routeDestinationPlaceId() {
            return this.routeEndpoint("unloadingLocations")?.place_id || "";
        },
    },
    mounted() {
        this.syncCalculatorPrefill();
        this.SET_FORM_BUTTONS([
            {
                label: "common.save", // i18n key
                event: "save-new-entry",
                color: "primary",
                dataCy: 'save-new-entry',
            },
        ]);
        this.$nextTick(() => {
            this.toolbarTeleportReady = !!document.getElementById(
                "form-toolbar-custom-actions"
            );
        });
        emitter.on("save-new-entry", this.validateForm);
    },
    unmounted() {
        this.toolbarTeleportReady = false;
        this.toolbarCalculatorOpen = false;
        this.toolbarFloorAreaOpen = false;
        this.toolbarFuelCalculatorOpen = false;
        this.appliedFloorAreaLabel = "";
        this.appliedTripDurationLabel = "";
        this.appliedFuelCostLabel = "";
        emitter.off("save-new-entry", this.validateForm);
        this.RESET_FORM_BUTTONS();
    },
    methods: {
        showAlertModal,
        ...mapMutations(["SET_FORM_BUTTONS", "RESET_FORM_BUTTONS"]),
        hasBothCountriesSet() {
            return !!(
                this.routeEndpoint("loadingLocations")?.country_code &&
                this.routeEndpoint("unloadingLocations")?.country_code
            );
        },
        routeEndpoint(listKey) {
            const rows = this.formData[listKey] || [];
            const dated = rows.filter((row) => row?.dateStart);
            if (!dated.length) {
                return listKey === "unloadingLocations" ? rows[rows.length - 1] : rows[0];
            }
            const sorted = this.sortedStopRows(dated);
            return listKey === "unloadingLocations" ? sorted[sorted.length - 1] : sorted[0];
        },
        sortedStopRows(rows) {
            const dated = [];
            const undated = [];
            rows.forEach((row, index) => {
                if (row?.dateStart) dated.push({ row, index });
                else undated.push({ row, index });
            });
            dated.sort((left, right) => {
                const leftKey = `${left.row.dateStart}T${left.row.timeStart || "00:00"}`;
                const rightKey = `${right.row.dateStart}T${right.row.timeStart || "00:00"}`;
                if (leftKey === rightKey) return left.index - right.index;
                return leftKey < rightKey ? -1 : 1;
            });
            undated.sort((left, right) => left.index - right.index);
            return [...dated.map((item) => item.row), ...undated.map((item) => item.row)];
        },
        onStopInput(listKey, index, value) {
            const current = this.formData[listKey]?.[index];
            if (!current) return;
            const nextRow = {
                ...value,
                rowKey: current.rowKey,
                id: current.id,
            };
            const next = this.formData[listKey].slice();
            next[index] = nextRow;
            this.formData[listKey] = this.sortedStopRows(next);
        },
        addStop(listKey) {
            this.formData[listKey] = [
                ...this.formData[listKey],
                this.createEmptyLocation(),
            ];
        },
        removeStop(listKey, index) {
            if ((this.formData[listKey] || []).length <= 1) return;
            this.formData[listKey] = this.formData[listKey].filter((_, rowIndex) => rowIndex !== index);
        },
        hasTruckAvgSpeedSet() {
            const speed = Number(this.formData.info?.avgSpeedKmh);
            return Number.isFinite(speed) && speed > 0;
        },
        syncCalculatorPrefill() {
            if (!this.editMode || !this.hasBothCountriesSet()) {
                return;
            }

            if (this.formType === "truck" && !this.hasTruckAvgSpeedSet()) {
                return;
            }

            const distance = this.formData.distance_km;
            if (distance != null && distance !== "") {
                this.calculatorDistanceKm = String(distance);
                if (!this.fuelDistanceKm) {
                    this.fuelDistanceKm = String(distance);
                }
            }

            if (this.formType === "truck") {
                const speed = this.formData.info?.avgSpeedKmh;
                if (speed !== "" && speed != null) {
                    this.calculatorSpeedKmh = String(speed);
                }
            }
        },
        applyPublishFloorArea(payload) {
            if (!payload?.resultLabel) return;

            this.floorAreaLengthCm = payload.lengthCm;
            this.floorAreaWidthCm = payload.widthCm;
            this.floorAreaPackageCount = payload.packageCount;
            this.appliedFloorAreaLabel = payload.resultLabel;
            this.toolbarFloorAreaOpen = false;
        },
        applyPublishTripDuration(payload) {
            if (!payload?.resultLabel) return;

            if (payload.distanceKm != null && payload.distanceKm !== "") {
                this.calculatorDistanceKm = payload.distanceKm;
                if (!this.fuelDistanceKm) {
                    this.fuelDistanceKm = payload.distanceKm;
                }
            }
            if (payload.speedKmh != null && payload.speedKmh !== "") {
                this.calculatorSpeedKmh = payload.speedKmh;
            }
            this.appliedTripDurationLabel = payload.resultLabel;
            this.toolbarCalculatorOpen = false;
        },
        applyPublishFuelCost(payload) {
            if (!payload?.resultLabel) return;

            this.fuelPricePerLiter = payload.pricePerLiter;
            this.fuelDistanceKm = payload.distanceKm;
            this.fuelConsumptionLPer100Km = payload.consumptionLPer100Km;
            this.appliedFuelCostLabel = payload.resultLabel;
            this.toolbarFuelCalculatorOpen = false;
        },
        onToolbarFuelCalculatorOpenChange(open) {
            this.toolbarFuelCalculatorOpen = open;
            if (open) {
                this.toolbarFloorAreaOpen = false;
                this.toolbarCalculatorOpen = false;
                if (
                    !this.fuelDistanceKm &&
                    (this.calculatorDistanceKm || this.formData.distance_km)
                ) {
                    this.fuelDistanceKm = String(
                        this.calculatorDistanceKm || this.formData.distance_km
                    );
                }
            }
        },
        onToolbarFloorAreaOpenChange(open) {
            this.toolbarFloorAreaOpen = open;
            if (open) {
                this.toolbarFuelCalculatorOpen = false;
                this.toolbarCalculatorOpen = false;
            }
        },
        onToolbarCalculatorOpenChange(open) {
            this.toolbarCalculatorOpen = open;
            if (open) {
                this.toolbarFuelCalculatorOpen = false;
                this.toolbarFloorAreaOpen = false;
            }
        },
        fillForm() {
            this.formData = {
                uid: uuidv4(),
                formType: this.formType,
                loadingLocations: [
                    {
                        ...this.createEmptyLocation(),
                        postalCode: "10001",
                        date: ["2025-08-01"],
                        dateStart: "2025-08-01",
                        dateEnd: "2025-08-01",
                        timeStart: "09:00",
                        timeEnd: "17:00",
                    },
                ],
                unloadingLocations: [
                    {
                        ...this.createEmptyLocation(),
                        postalCode: "90001",
                        date: ["2025-08-03"],
                        dateStart: "2025-08-03",
                        dateEnd: "2025-08-03",
                        timeStart: "10:00",
                        timeEnd: "18:00",
                    },
                ],
                info: {
                    weight: 20,
                    volume: 30,
                    mdp: 40,
                    price: 500,
                    paymentTerm: 2,
                    currency: "EUR",
                    vatType: true,
                },
                notes: "This is a sample note.",
                transportOptions:
                    this.formType === "truck"
                        ? {
                              truckTransportType: 2,
                              truckCoating: 2,
                              truckType: 1,
                              truckSpecialFeatures: [1],
                              dangerousLoad: false,
                          }
                        : {
                              platformType: [2],
                              truckCoating: 2,
                              dangerousLoad: false,
                              truckTransportType: 2,
                          },
            };
        },
        getTruckType(value) {
            return mapEnumValue(TRUCK_TYPE, value);
        },
        getTransportType(value) {
            return mapEnumValue(TRUCK_TRANSPORT_TYPE, value);
        },
        getSpecialFeature(value) {
            return mapEnumValue(TRUCK_SPECIAL_FEATURE, value);
        },
        getCoating(value) {
            return mapEnumValue(TRUCK_COATING, value);
        },
        getPaymentTerm(value) {
            return mapEnumValue(PAYMENT_DEADLINE, value);
        },
        /**
         * Creates a properly structured location object.
         * @returns {Object} - Empty location structure.
         */
        createEmptyLocation() {
            return {
                rowKey: uuidv4(),
                country_code: "",
                place_id: "",
                place_display_name: "",
                postalCode: "",
                date: [],
                dateStart: null,
                dateEnd: null,
                timeStart: null,
                timeEnd: null,
            };
        },
        async validateLocationList(refName) {
            const refs = this.$refs[refName];
            const list = Array.isArray(refs) ? refs : refs ? [refs] : [];
            let isValid = true;
            for (const component of list) {
                if (component?.validateFields) {
                    const valid = await component.validateFields();
                    if (!valid) isValid = false;
                }
            }
            return isValid;
        },
        resetLocationList(refName) {
            const refs = this.$refs[refName];
            const list = Array.isArray(refs) ? refs : refs ? [refs] : [];
            list.forEach((component) => component?.resetValidation?.());
        },
        // * Handles form submission logic.
        //  */
        async validateForm() {
            await nextTick(); // Ensures all refs are available
            let isValid = true;

            if (this.$refs.form) {
                const formResult = await this.$refs.form.validate();
                if (!formResult?.valid) {
                    isValid = false;
                }
            }

            if (this.$refs.formInfo?.validateFields) {
                const infoValid = await this.$refs.formInfo.validateFields();
                if (!infoValid) {
                    isValid = false;
                }
            }

            if (this.$refs.formOptions?.validateFields) {
                const optionsValid =
                    await this.$refs.formOptions.validateFields();
                if (!optionsValid) {
                    isValid = false;
                }
            }

            if (!(await this.validateLocationList("loadingLocationFields"))) {
                isValid = false;
            }
            if (!(await this.validateLocationList("unloadingLocationFields"))) {
                isValid = false;
            }

            if (isValid) {
                await this.submitFormTemporar();
            } else {
                this.showAlertModal(
                    this.$store,
                    this.$t("validation.validationFailed"),
                    "warning",
                    5000
                );
            }
        },
        /**
         * Resets the form to its initial state and clears validation errors.
         */
        resetForm() {
            this.calculatorDistanceKm = "";
            this.calculatorSpeedKmh = "";
            this.toolbarCalculatorOpen = false;
            // Reset form data
            this.formData = {
                uid: uuidv4(),
                formType: this.formType,
                loadingLocations: [this.createEmptyLocation()],
                unloadingLocations: [this.createEmptyLocation()],
                info: {
                    weight: "",
                    volume: "",
                    mdp: "",
                    price: "",
                    currency: "EUR",
                    vatType: false,
                    paymentTerm: 1,
                    avgSpeedKmh: "",
                },
                notes: "",
                transportOptions: {
                    truckCoating: 1,
                    truckTransportType: 1,
                    truckType: "",
                    truckSpecialFeatures: "",
                    dangerousLoad: false,
                    platformType: [],
                },
            };

            // Reset validation
            if (this.$refs.form) this.$refs.form.resetValidation();
            if (this.$refs.formInfo) this.$refs.formInfo.resetValidation();
            if (this.$refs.formOptions)
                this.$refs.formOptions.resetValidation();
            this.resetLocationList("loadingLocationFields");
            this.resetLocationList("unloadingLocationFields");

            // Reset fields in child components
            if (this.$refs.formInfo) this.$refs.formInfo.resetFields();
            if (this.$refs.formOptions) this.$refs.formOptions.resetFields();
        },
        /**
         * Cleans a location object by removing unnecessary fields.
         * Ensures that only relevant data is sent.
         */
        async submitFormTemporar() {
            this.submitting = true;
            try {
            const allowedFields = [
                "uid",
                "info",
                "transportOptions",
                "loadingLocations",
                "unloadingLocations",
                "notes",
            ];

            let filteredFormData = Object.fromEntries(
                Object.entries(this.formData).filter(([key]) =>
                    allowedFields.includes(key)
                )
            );

            // Ensure the form type is included
            filteredFormData.form_type = this.formType;

            // Ensure all nested objects are properly structured before sending
            filteredFormData.loading_locations = (
                filteredFormData.loadingLocations || []
            ).map((location) => this.cleanLocation(location));
            filteredFormData.unloading_locations = (
                filteredFormData.unloadingLocations || []
            ).map((location) => this.cleanLocation(location));
            delete filteredFormData.loadingLocations;
            delete filteredFormData.unloadingLocations;
            filteredFormData.info = this.cleanInfo(filteredFormData.info);
            filteredFormData.transport_options = this.cleanTransportOptions(
                filteredFormData.transportOptions
            );

            // Determine the endpoint based on form type and edit mode
            let endpoint = "";
            if (this.editMode) {
                // Update (edit) mode: use PATCH
                endpoint =
                    this.formType === "truck"
                        ? `${process.env.VUE_APP_API_URL}/truck-requests/${filteredFormData.uid}/`
                        : `${process.env.VUE_APP_API_URL}/ware-requests/${filteredFormData.uid}/`;
            } else {
                // Create mode: use POST
                endpoint =
                    this.formType === "truck"
                        ? `${process.env.VUE_APP_API_URL}/truck-requests/create/`
                        : `${process.env.VUE_APP_API_URL}/ware-requests/create/`;
            }

            try {
                let response;
                if (this.editMode) {
                    response = await axios.patch(endpoint, filteredFormData, {
                        headers: { "Content-Type": "application/json" },
                    });
                } else {
                    response = await axios.post(endpoint, filteredFormData, {
                        headers: { "Content-Type": "application/json" },
                    });
                }

                // Show success alert
                this.showAlertModal(
                    this.$store,
                    this.$t("validation.formSubmitted"),
                    "success",
                    5000
                );

                // Redirect to correct list based on form type
                if (this.formType === "truck") {
                    this.$router.push({ name: "MyTrucks" }); // Redirect to My Trucks
                } else {
                    this.$router.push({ name: "MyWares" }); // Redirect to My Wares
                }
            } catch (error) {
                this.showAlertModal(
                    this.$store,
                    this.$t("common.submissionFailed"),
                    "danger",
                    12000,
                    error
                );
            }
            } finally {
                this.submitting = false;
            }
        },

        /**
         * Cleans the location object before submitting to the backend.
         */
        cleanLocation(location) {
            if (!location) return {};
            const cleaned = {
                country_code: location.country_code || "",
                place_id: location.place_id || null,
                place_display_name: location.place_display_name || "",
                postal_code: location.postalCode || "",
                date_start: location.dateStart || null,
                date_end: location.dateEnd || null,
                date: location.date || [],
                time_start: location.timeStart || null,
                time_end: location.timeEnd || null,
            };
            if (location.id) cleaned.id = location.id;
            return cleaned;
        },

        /**
         * Cleans the info object before sending it to the backend.
         */
        cleanInfo(info) {
            return {
                weight: parseFloat(info.weight) || 0,
                volume: info.volume !== "" ? parseFloat(info.volume) : null,
                mdp: parseFloat(info.mdp) || 0,
                price: info.price !== "" ? parseFloat(info.price) : null,
                payment_term: parseInt(info.paymentTerm) || 1, // Ensure this is an integer
                currency: info.currency,
                vat_type: info.vatType === true,
                avg_speed_kmh:
                    this.formType === "truck"
                        ? parseFloat(info.avgSpeedKmh) || null
                        : null,
            };
        },

        /**
         * Cleans the transport options based on the form type.
         */
        cleanTransportOptions(transportOptions) {
            if (!transportOptions) return {};

            return this.formType === "truck"
                ? {
                      truck_transport_type:
                          transportOptions.truckTransportType ?? null,
                      truck_special_features:
                          transportOptions.truckSpecialFeatures ?? [],
                      truck_coating: transportOptions.truckCoating ?? null,
                      truck_type: transportOptions.truckType ?? null,
                  }
                : {
                      truck_coating: transportOptions.truckCoating ?? null,
                      platform_type: transportOptions.platformType ?? [],
                      truck_special_features:
                          transportOptions.truckSpecialFeatures ?? [],
                      escrow_account: transportOptions.escrowAccount ?? false,
                      dangerous_load: transportOptions.dangerousLoad ?? false,
                      truck_transport_type:
                          transportOptions.truckTransportType ?? null,
                  };
        },
    },
    watch: {
        entriesData: {
            handler(newVal) {
                if (this.editMode && newVal) {
                    this.formData = { ...newVal };
                    this.syncCalculatorPrefill();
                }
            },
            immediate: true,
            deep: true,
        },
        "formData.loadingLocations": {
            handler() {
                this.syncCalculatorPrefill();
            },
            deep: true,
        },
        "formData.unloadingLocations": {
            handler() {
                this.syncCalculatorPrefill();
            },
            deep: true,
        },
        submitRequested(newVal) {
            if (newVal) {
                this.validateForm();
                // Reset the flag after submission.
                this.$store.commit("RESET_SUBMIT");
            }
        },
    },
};
</script>
